import type {ReadReceipt} from '../../hook/useSharedMessaging';
import type {Message, MessageEdge, MessageSender} from './types';

export const isCurrentSideReceipt = (receipt: ReadReceipt, isInternalAuth: boolean, currentUserGuid?: string): boolean =>
    receipt.internal === isInternalAuth || receipt.userGuid === currentUserGuid;

export const getSideLastReadAt = (receipts: ReadReceipt[], isInternalAuth: boolean, currentUserGuid?: string): Date | undefined => {
    const sideReadTimes = receipts
        .filter((receipt) => isCurrentSideReceipt(receipt, isInternalAuth, currentUserGuid) && receipt.lastReadAt)
        .map((receipt) => new Date(receipt.lastReadAt).getTime());
    return sideReadTimes.length === 0 ? undefined : new Date(Math.max(...sideReadTimes));
};

export const appendMessage = (messages: Message[], message: Message): Message[] => {
    return messages.some((item) => item.guid === message.guid) ? messages : [...messages, message];
};

export const mergeMessages = (current: Message[], incoming: Message[]): Message[] => {
    const messagesByGuid = new Map<string, Message>();
    [...current, ...incoming].forEach((message) => messagesByGuid.set(message.guid, message));
    return Array.from(messagesByGuid.values()).sort((left, right) => new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime());
};

export const messagesFromEdges = (edges: MessageEdge[]): Message[] => {
    return edges.map((edge) => edge.node).reverse();
};

export const cacheSenders = (messages: Message[], senderCache: Map<string, MessageSender>) => {
    messages.forEach((message) => {
        if (message.sender) {
            senderCache.set(message.sender.guid, message.sender);
        }
    });
};

export const getLatestUrgentState = (messages: Message[]): boolean | null => {
    const latestUrgentFlagMessage = messages.reduce<Message | null>((latest, message) => {
        if (message.messageType === 'URGENT_FLAG_ON' || message.messageType === 'URGENT_FLAG_OFF') {
            return !latest || new Date(message.createdAt) > new Date(latest.createdAt) ? message : latest;
        }
        return latest;
    }, null);
    return latestUrgentFlagMessage ? latestUrgentFlagMessage.messageType === 'URGENT_FLAG_ON' : null;
};

export const findUnreadDividerIndex = (
    messages: Message[],
    lastReadAt: Date | null | undefined,
    isInternalAuth: boolean,
): number => {
    if (lastReadAt === undefined) return -2;

    const lastReadTime = lastReadAt === null ? Number.NEGATIVE_INFINITY : lastReadAt.getTime();
    // Divider sits before the first other-side activity after the read cursor — system
    // messages (urgent flag, offer events) count for placement even though only TEXT
    // messages increment the unread count.
    const firstUnreadIndex = messages.findIndex(
        (message) => message.internal !== isInternalAuth && new Date(message.createdAt).getTime() > lastReadTime,
    );

    if (firstUnreadIndex === -1) return -2;
    if (firstUnreadIndex === 0) return -1;
    return firstUnreadIndex - 1;
};

export const findLatestReceivedMessageAtOrBefore = (
    messages: Message[],
    cutoff: Date,
    isInternalAuth: boolean,
): Message | undefined => messages.slice().reverse().find((message) =>
    message.messageType === 'TEXT' &&
    message.internal !== isInternalAuth &&
    new Date(message.createdAt).getTime() <= cutoff.getTime()
);

export const getLastReadMessageGuidAfterMarkAsUnread = (
    messages: Message[],
    isInternalAuth: boolean,
): string | null => {
    const latestMessage = messages.slice().reverse().find((message) => message.messageType === 'TEXT');
    if (!latestMessage || latestMessage.internal === isInternalAuth) return null;
    const latestMessageTime = new Date(latestMessage.createdAt).getTime();
    const currentSideMessages = messages.filter(
        (message) => message.messageType === 'TEXT' && message.internal === isInternalAuth && new Date(message.createdAt).getTime() < latestMessageTime,
    );

    if (currentSideMessages.length === 0) return null;

    currentSideMessages.sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime());
    return currentSideMessages[0].guid;
};

export const countUnreadMessages = (
    messages: Message[],
    lastReadAt: Date | null | undefined,
    isInternalAuth: boolean,
): number => {
    if (lastReadAt === undefined) return 0;

    const lastReadTime = lastReadAt === null ? Number.NEGATIVE_INFINITY : lastReadAt.getTime();
    return messages
        .filter((message) => message.messageType === 'TEXT' && message.internal !== isInternalAuth)
        .filter((message) => new Date(message.createdAt).getTime() > lastReadTime).length;
};
